import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Button,
  Card,
  Form,
  Input,
  List,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Typography,
  message
} from 'antd'
import { CheckCircleTwoTone, ExclamationCircleTwoTone } from '@ant-design/icons'
import PageTitle from 'components/PageTitle'
import {
  getRequest,
  growthConfigUrl,
  growthSourceUrl,
  growthSourcesUrl,
  growthStatusUrl,
  patchRequest,
  postRequest
} from '../../config'
import { setupChecklist } from './utils'

const { Paragraph, Text } = Typography

const SOURCE_KINDS = [
  { value: 'official', label: 'Official event site' },
  { value: 'database', label: 'Event database' },
  { value: 'diaspora', label: 'Diaspora' },
  { value: 'partners', label: 'Partners' }
]

// Setup and switches for the growth bot (spec: 0042_growth_bot Req 5).
const SettingsPage = () => {
  const [status, setStatus] = useState(null)
  const [sources, setSources] = useState([])
  const [saving, setSaving] = useState(false)
  const [sourceModal, setSourceModal] = useState(null)
  const [form] = Form.useForm()
  const [sourceForm] = Form.useForm()

  const loadStatus = () =>
    getRequest(growthStatusUrl, 'bot status', () => {}).then(response => {
      if (response.success) setStatus(response.data)
    })

  const loadSources = () =>
    getRequest(growthSourcesUrl, 'sources', () => {}).then(response => {
      if (response.success) setSources(response.data || [])
    })

  useEffect(() => {
    loadStatus()
    loadSources()
    getRequest(growthConfigUrl, 'settings', () => {}).then(response => {
      if (response.success) form.setFieldsValue(response.data)
    })
  }, [form])

  useEffect(() => {
    if (!sourceModal) return
    sourceForm.resetFields()
    sourceForm.setFieldsValue(sourceModal.id ? sourceModal : { kind: 'official', isActive: true })
  }, [sourceModal, sourceForm])

  const saveConfig = async () => {
    const values = await form.validateFields()
    const response = await patchRequest(
      growthConfigUrl,
      { ...values, chatId: (values.chatId || '').trim() },
      'settings',
      setSaving
    )
    if (response.success) {
      message.success('Saved.')
      loadStatus()
    } else {
      const data = response.error?.response?.data || {}
      if (data.timezone) message.error(String(data.timezone))
    }
  }

  const saveSource = async () => {
    const values = await sourceForm.validateFields()
    const response = sourceModal.id
      ? await patchRequest(growthSourceUrl(sourceModal.id), values, 'source', () => {})
      : await postRequest(growthSourcesUrl, values, 'source', () => {})
    if (response.success) {
      setSourceModal(null)
      loadSources()
    }
  }

  const toggleSource = async source => {
    const response = await patchRequest(growthSourceUrl(source.id), { isActive: !source.isActive }, 'source', () => {})
    if (response.success) loadSources()
  }

  /* eslint-disable react/display-name */
  const sourceColumns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, r) => (
        <a href={r.url} target="_blank" rel="noopener noreferrer">
          {r.name}
        </a>
      )
    },
    { title: 'Kind', key: 'kind', render: (_, r) => (SOURCE_KINDS.find(k => k.value === r.kind) || {}).label || r.kind },
    { title: 'On', key: 'on', render: (_, r) => <Switch size="small" checked={r.isActive} onChange={() => toggleSource(r)} /> },
    {
      title: '',
      key: 'edit',
      render: (_, r) => (
        <Button size="small" type="link" onClick={() => setSourceModal(r)}>
          Edit
        </Button>
      )
    }
  ]
  /* eslint-enable react/display-name */

  return (
    <div className="container-fluid">
      <PageTitle title="Growth · Settings" />
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card title="Setup" loading={!status}>
          {status ? (
            <>
              <List
                dataSource={setupChecklist(status)}
                renderItem={step => (
                  <List.Item>
                    <Space align="start">
                      {step.ok ? (
                        <CheckCircleTwoTone twoToneColor="#52c41a" />
                      ) : (
                        <ExclamationCircleTwoTone twoToneColor="#faad14" />
                      )}
                      <div>
                        <Text strong>{step.label}</Text>
                        {!step.ok ? <Paragraph type="secondary" style={{ marginBottom: 0 }}>{step.todo}</Paragraph> : null}
                      </div>
                    </Space>
                  </List.Item>
                )}
              />
              {status.username ? <Paragraph>Bot: @{status.username}</Paragraph> : null}
              <Paragraph type="secondary">
                Who can use the bot is set on the <Link to="/inventory-assistant/people">People page</Link>: tick
                Wabenzy Growth Bot for each person.
              </Paragraph>
            </>
          ) : null}
        </Card>

        <Card title="Switches">
          <Form form={form} layout="vertical" style={{ maxWidth: 420 }}>
            <Form.Item name="enabled" label="Scheduled digests, reminders and review" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="chatId" label="Telegram chat id" extra="Send /start to the bot; it replies with this.">
              <Input placeholder="e.g. 123456789" />
            </Form.Item>
            <Form.Item name="timezone" label="Timezone" rules={[{ required: true }]}>
              <Input placeholder="Europe/Berlin" />
            </Form.Item>
            <Button type="primary" onClick={saveConfig} loading={saving}>
              Save
            </Button>
          </Form>
          {status ? (
            <Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
              Schedule: {status.schedule}
            </Paragraph>
          ) : null}
        </Card>

        <Card
          title="Where the searches start"
          extra={
            <Button onClick={() => setSourceModal({})} type="primary">
              Add source
            </Button>
          }
        >
          <Paragraph type="secondary">
            Starting points, not limits: the search goes wider. Switch one off to stop suggesting it.
          </Paragraph>
          <Table rowKey="id" size="small" dataSource={sources} columns={sourceColumns} pagination={false} scroll={{ x: 500 }} />
        </Card>
      </Space>

      <Modal
        title={sourceModal && sourceModal.id ? 'Edit source' : 'Add source'}
        open={Boolean(sourceModal)}
        okText="Save"
        onOk={saveSource}
        onCancel={() => setSourceModal(null)}
        centered
      >
        <Form form={sourceForm} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="url" label="Link" rules={[{ required: true, type: 'url' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="kind" label="Kind">
            <Select options={SOURCE_KINDS} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input />
          </Form.Item>
          <Form.Item name="isActive" label="On" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default SettingsPage
